import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Row, Modal, Button, Card, Spinner } from 'react-bootstrap';
import { fetchPortfolioData, fetchPromptsData } from './api';
import UserInformationList from '../Preview/UserInformationList';
import WelcomeScreen from './WelcomeScreen';
import PortfolioUpdateFlow from '../UserProfile/PortfolioUpdateFlow';
import { ReturnedPortfolio } from '../Create-Portfolio/types';
import { API_ROUTES } from '../../config/api-routes';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import axiosInstance from '../../api/api';
import '../Preview/Preview.css';
import './WelcomeScreen.css';
import { UserType } from '../../App';
import OnboardingProgress from '../OnboardingProgress';
import ResumeUpload from '../ResumeUpload';
import {
  normalizePromptListResponse,
  profileFieldAsString,
  syncCurrentUserFromApi,
  userNeedsPromptSetup,
} from '../../utils/userProfile';
import { getSessionStore } from '../../store/sessionStore';
import { PageBusyLoader } from '../ui/PageBusyLoader';
import { toast } from 'react-toastify';

const MAX_PROFILE_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const resumePanelRef = useRef<HTMLDivElement>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [portfolioId, setPortfolioId] = useState<number>(0);
  const [portfolioData, setPortfolioData] = useState<ReturnedPortfolio>({
    overview: '',
    projects: [],
    references: [],
    languages: [],
    certifications: [],
    workCulture: [],
    accomplishments: [],
    organizations: [],
  });
  const [userData, setUserData] = useState<UserType | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const accessToken = localStorage.getItem('accessToken');

  const handleEditAvatar = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }

    if (file.size > MAX_PROFILE_IMAGE_SIZE_BYTES) {
      toast.error('Image must be 10MB or smaller.');
      return;
    }

    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append('profileImg', file);
      const response = await axiosInstance.put(API_ROUTES.USER.UPDATE_USER, formData);
      if (!response || response.status !== 200) {
        throw new Error('Upload failed');
      }

      const updatedImg =
        response.data?.profileImg ||
        response.data?.user?.profileImg ||
        response.data?.data?.profileImg;

      if (updatedImg) {
        setUserData((prev) => (prev ? { ...prev, profileImg: updatedImg } : prev));
        const store = getSessionStore();
        if (store.currentUser) {
          store.updateCurrentUser({ profileImg: updatedImg });
        } else if (userData) {
          store.setCurrentUser({ ...userData, profileImg: updatedImg });
        }
      } else {
        await getData();
      }

      window.dispatchEvent(new Event('profile-updated'));
      toast.success('Profile photo updated.');
    } catch (error) {
      console.error('Avatar upload failed:', error);
      toast.error('Failed to upload profile photo.');
    } finally {
      setAvatarUploading(false);
    }
  };

  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus === 'profile-photo') {
      window.setTimeout(() => avatarInputRef.current?.click(), 250);
      return;
    }
    if (focus === 'resume') {
      window.setTimeout(
        () =>
          resumePanelRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          }),
        250
      );
    }
  }, [searchParams]);

  const getData = useCallback(async () => {
    try {
      setLoader(true);
      const portfolioData = await fetchPortfolioData();
      const promptsData = await fetchPromptsData();

      const response = await axiosInstance.get('user/current-user');
      if (!response || response.status !== 200) {
        setLoader(false);
        return;
      }

      const data = response.data;
      const userRecord = (data?.user ?? data) as Record<string, unknown> | undefined;

      if (userRecord) {
        syncCurrentUserFromApi(userRecord);
        const formattedUser: UserType = {
          id: Number(userRecord.id),
          firstName: String(userRecord.firstName || ''),
          lastName: String(userRecord.lastName || ''),
          email: String(userRecord.email || ''),
          gender: String(userRecord.gender || ''),
          dateOfBirth: String(userRecord.dateOfBirth || ''),
          country: String(userRecord.country || ''),
          state: String(userRecord.state || ''),
          city: String(userRecord.city || ''),
          profileImg: String(userRecord.profileImg || ''),
          resume: String(userRecord.resume || ''),
          resumeFileName:
            profileFieldAsString(userRecord.resumeFileName) ||
            profileFieldAsString(userRecord.resume_filename),
          phoneNumber: String(userRecord.phoneNumber || ''),
          timeToStart: String(userRecord.timeToStart || ''),
          willingToRelocate: String(userRecord.willingToRelocate || ''),
          company: String(userRecord.company || ''),
          workArrangements: String(userRecord.workArrangements || ''),
          linkedin: String(userRecord.linkedin || ''),
          otherSocial: String(userRecord.otherSocial || ''),
        };
        setUserData(formattedUser);
      }

      if (portfolioData) {
        setPortfolioData({
          isOverviewEnabled: portfolioData.isOverviewEnabled,
          isProjectsEnabled: portfolioData.isProjectsEnabled,
          isReferencesEnabled: portfolioData.isReferencesEnabled,
          isLanguagesEnabled: portfolioData.isLanguagesEnabled,
          isCertificationsEnabled: portfolioData.isCertificationsEnabled,
          isWorkCultureEnabled: portfolioData.isWorkCultureEnabled,
          isOrganizationsEnabled: portfolioData.isOrganizationsEnabled,
          isAccomplishmentsEnabled: portfolioData.isAccomplishmentsEnabled,
          overview: portfolioData.overview || '',
          projects: portfolioData.projects || [],
          references: portfolioData.references || [],
          languages: portfolioData.languages || [],
          certifications: portfolioData.certifications || [],
          workCulture: portfolioData.workCulture || [],
          accomplishments: portfolioData.accomplishments || [],
          organizations: portfolioData.organizations || [],
        });
        setPortfolioId(portfolioData.id);
        getSessionStore().setPortfolio(portfolioData);
      }

      const promptList = normalizePromptListResponse(promptsData);
      getSessionStore().setPrompts(promptList);

      if (!userNeedsPromptSetup(promptList.length, userRecord)) {
        navigate('/dashboard', { replace: true });
        return;
      }

      setLoader(false);
    } catch (error: any) {
      console.error('Error fetching user info:', error);
    } finally {
      setLoader(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (accessToken) {
      getData();
    }
  }, [accessToken, getData]);

  useEffect(() => {
    const handlePromptsUpdated = () => {
      void getData();
    };
    window.addEventListener('prompts-updated', handlePromptsUpdated);
    return () => window.removeEventListener('prompts-updated', handlePromptsUpdated);
  }, [getData]);

  const handleDeletePortfolio = async () => {
    if (!portfolioId) return;
    const response = await axiosInstance.delete(
      `${API_ROUTES.PORTFOLIO.DELETE_PORTFOLIO}/${portfolioId}`
    );
    if (!response || response.status !== 200) return;
    setPortfolioData({
      overview: '',
      projects: [],
      references: [],
      languages: [],
      certifications: [],
      workCulture: [],
      accomplishments: [],
      organizations: [],
    });
    setShowModal(false);
  };

  return (
    <>
      {loader ? (
        <PageBusyLoader pageName="Getting Started" />
      ) : (
        <div className="container">
          <Row>
            <OnboardingProgress />
            <Col className="mb-5">
              <Row className="row-sticky welcome-page-columns">
                <Col md={4} sm={12} className="welcome-page-profile order-2 order-md-0">
                  <Card className="profile-card mb-3">
                    <div className="profile-header">
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        className="d-none"
                        aria-hidden
                        tabIndex={-1}
                        onChange={handleAvatarFileChange}
                      />
                      <div className="profile-image-wrapper">
                        {userData?.profileImg ? (
                          <div style={{ position: 'relative' }}>
                            <img
                              src={userData.profileImg || '/avatar.png'}
                              alt="Profile Avatar"
                              className="profile-avatar profile-photo"
                              onClick={handleEditAvatar}
                              style={{ cursor: 'pointer' }}
                            />
                            <button
                              type="button"
                              title="Edit Avatar"
                              className="user-info-edit-button profile-avatar-edit-btn"
                              onClick={handleEditAvatar}
                              disabled={avatarUploading}
                            >
                              {avatarUploading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <EditIcon style={{ width: '15px', height: '15px' }} />
                              )}
                            </button>
                          </div>
                        ) : (
                          <div style={{ position: 'relative' }}>
                            <img
                              src={userData?.profileImg || '/avatar.png'}
                              alt="Profile Avatar"
                              className="profile-avatar profile-photo"
                              onClick={handleEditAvatar}
                              style={{ cursor: 'pointer' }}
                            />
                            <button
                              type="button"
                              title="Edit Avatar"
                              className="user-info-edit-button profile-avatar-edit-btn"
                              onClick={handleEditAvatar}
                              disabled={avatarUploading}
                            >
                              {avatarUploading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <EditIcon style={{ width: '15px', height: '15px' }} />
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      <h3 className="profile-name">
                        {userData?.firstName} {userData?.lastName}
                      </h3>
                      <span className="profile-role">Candidate</span>
                    </div>
                    <Card.Body className="profile-body">
                      <h3 className="profile-name">
                        {/* {`${userData.firstName} ${userData.lastName}`} */}
                      </h3>

                      {userData && (
                        <div ref={resumePanelRef} className="welcome-resume-panel">
                          <ResumeUpload
                            existingResumeUrl={userData.resume}
                            existingResumeFileName={userData.resumeFileName}
                            uploadImmediately
                            showLabel={false}
                            displayMode="panel"
                            onUploadSuccess={(fileUrl, fileName) => {
                              setUserData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      resume: fileUrl,
                                      resumeFileName: fileName || prev.resumeFileName,
                                    }
                                  : prev
                              );
                              getSessionStore().updateCurrentUser({
                                resume: fileUrl,
                                resumeFileName: fileName,
                              });
                              window.dispatchEvent(new Event('profile-updated'));
                              getData();
                            }}
                          />
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                  <UserInformationList userData={userData} onTriggerUpdateFlow={getData} />
                </Col>
                <Col md={8} sm={12} className="welcome-page-cta order-1 order-md-0">
                  <WelcomeScreen user={userData} setUser={setUserData} refreshUser={getData} />
                </Col>
              </Row>
            </Col>
          </Row>

          <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
            <Modal.Header>
              <Modal.Title>Save Successful</Modal.Title>
            </Modal.Header>
            <Modal.Body>Your changes have been saved successfully!</Modal.Body>
            <Modal.Footer>
              <Button variant="success" onClick={() => setShowSaveModal(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showModal} onHide={() => setShowModal(false)} centered>
            <Modal.Header>
              <Modal.Title>Confirm Deletion</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to permanently delete your portfolio?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeletePortfolio}>
                Delete
              </Button>
            </Modal.Footer>
          </Modal>

          <PortfolioUpdateFlow
            show={showOnboardingModal}
            onClose={() => setShowOnboardingModal(false)}
            userData={userData}
            refreshUser={getData}
          />
        </div>
      )}
      {portfolioData?.overview && <div className="portfolio-overview"></div>}
    </>
  );
};

export default WelcomePage;
